/**
 * MultiChoice drill strategy (Phase 3, design §1) — pure ports of today's
 * MultiChoice branches in useStep / StepView / useMultiChoice / the
 * MultiChoice cards.
 */
import { orderArray } from 'shared/utilities/array';
import type { StackChoices } from 'src/types/components';
import type { MultichoiceTry } from 'src/types/course/step';
import { escapeText } from './escapeText';
import type { DrillItemMap, DrillResultRowMap, DrillStrategy } from './types';

type Choice = StackChoices['attributes'];
type MultiChoiceRow = DrillResultRowMap['MultiChoice'];

/**
 * "Your answer" HTML (port of useMultiChoice.multiChoiceFeedbackAnswer).
 * Picks in answer order (right = positive, wrong = negative), then every
 * missed correct choice. Choice text is HTML-escaped (BUG-21).
 */
export function multiChoiceFeedbackHtml(
  answer: string[],
  correct: string[] | undefined,
): string {
  let mcFeedback = '<p class="text-border q-my-none">';
  // One counter across both loops: the separator only follows a first item
  // (an empty answer no longer starts with ", " — BUG-22).
  let count = 0;
  answer.forEach((item) => {
    const className = correct?.includes(item)
      ? 'text-positive'
      : 'text-negative';
    mcFeedback += `${count++ > 0 ? ', ' : ' '}`;
    mcFeedback += `<span class="${className}">${escapeText(item)}</span>`;
  });
  correct?.forEach((item: string) => {
    if (answer.includes(item)) return;
    mcFeedback += `${count++ > 0 ? ', ' : ' '}`;
    mcFeedback += `<span class="text-border">${escapeText(item)}</span>`;
  });
  mcFeedback += '</p>';
  return mcFeedback;
}

/**
 * The picks must equal the correct values as a set, with no duplicate picks
 * (BUG-22 fixed: a duplicated correct value no longer lets a wrong pick pass).
 * Duplicate correct values collapse: the solve card's checkboxes share a
 * value, so they toggle together and the answer can hold it only once.
 */
export function multiChoiceIsCorrect(
  answer: string[],
  correct: string[] | undefined,
): boolean {
  if (!Array.isArray(correct)) return false;
  const picked = new Set(answer);
  const expected = new Set(correct);
  return (
    picked.size === answer.length &&
    picked.size === expected.size &&
    [...expected].every((item) => picked.has(item))
  );
}

/**
 * Port of MultiChoice StepSolveCard's `currentStackItem` watcher: keeps the
 * previous order (returns `previous` itself) while every shown choice is still
 * one of the item's choices (reference equality); otherwise exactly one
 * `orderArray(…, 'random')` call over the item's choices.
 */
export function multiChoiceShuffle(
  previous: Choice[],
  item: { choices?: Choice[] | null } | undefined,
): Choice[] {
  if (previous.length > 0 && !previous.some((c) => !item?.choices?.includes(c)))
    return previous;
  return orderArray(item?.choices ?? [], 'random');
}

function correctAnswer(
  item: DrillItemMap['MultiChoice'],
): string[] | undefined {
  return item?.choices
    .filter((choice: Choice) => choice?.correct)
    .map((choice: Choice) => choice?.value) as string[] | undefined;
}

const errorRow: MultiChoiceRow = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const multiChoiceStrategy: DrillStrategy<'MultiChoice'> = {
  kind: 'MultiChoice',
  speaksQuestion: true,
  speaksAnswer: true,
  errorRow,

  correctAnswer,

  isCorrect(answer, item) {
    if (answer === undefined || answer === null) return false;
    return multiChoiceIsCorrect(answer, correctAnswer(item));
  },

  feedbackAnswerHtml(answer, item) {
    return multiChoiceFeedbackHtml(answer, correctAnswer(item));
  },

  feedbackCorrectAnswer() {
    return '';
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  // Same as StepView's getHelp: throws when there is no correct list.
  // Rendered via v-html: escaped (BUG-21).
  helpAnswer(item) {
    return escapeText((correctAnswer(item) as string[]).join(', '));
  },

  // Spoken as one string, picks joined like the help text (BUG-25 fixed).
  spokenAnswer(answer) {
    return Array.isArray(answer) ? answer.join(', ') : answer;
  },

  questionText(item) {
    return (item?.source as string | undefined) || '';
  },

  resultRow(item, id, tries) {
    return {
      id,
      source: (item?.source as string | undefined) ?? '',
      correct: item?.choices
        ?.filter((choice: Choice) => choice?.correct)
        .map((choice: Choice) => (choice?.value as string | undefined) ?? ''),
      // Stored tries carry the raw emitted array (BUG-15).
      tries: tries as unknown as MultichoiceTry[],
    };
  },
};
